// Time window (ms) for matching optimistic tasks with realtime inserts
// 5 seconds accommodates slow 3G connections while minimizing false positives
export const OPTIMISTIC_TASK_MATCH_WINDOW_MS = 5000

/**
 * Determines if an optimistic task matches a real task from the database.
 * Used to prevent duplicates when realtime INSERT events arrive before fulfilled actions.
 *
 * Matching criteria:
 * - Task must be marked as optimistic
 * - created_at timestamps within 5-second window
 * - Identical title, context, status, effort_level, and user_id
 *
 * @param {Object} optimisticTask - The optimistic task from Redux state
 * @param {Object} realTask - The real task from realtime or API
 * @returns {boolean} True if tasks match, false otherwise
 */
export const isOptimisticTaskMatch = (optimisticTask, realTask) => {
  // Must be an optimistic task
  if (!optimisticTask._isOptimistic) return false

  // Both must have timestamps
  if (!optimisticTask.created_at || !realTask.created_at) return false

  const taskTime = new Date(optimisticTask.created_at).getTime()
  const recordTime = new Date(realTask.created_at).getTime()

  // Guard against invalid timestamps
  if (isNaN(taskTime) || isNaN(recordTime)) {
    if (import.meta.env.DEV) {
      console.warn('[TaskMatching] Invalid timestamp in optimistic match:', {
        optimisticCreatedAt: optimisticTask.created_at,
        realCreatedAt: realTask.created_at
      })
    }
    return false
  }

  const timeDiff = Math.abs(taskTime - recordTime)

  // Match on time window and multiple fields for specificity
  // This prevents false positives when users create similar tasks rapidly
  // user_id ensures we don't match tasks from different users/sessions
  return (
    timeDiff < OPTIMISTIC_TASK_MATCH_WINDOW_MS &&
    optimisticTask.title === realTask.title &&
    optimisticTask.context === realTask.context &&
    optimisticTask.status === realTask.status &&
    optimisticTask.effort_level === realTask.effort_level &&
    optimisticTask.user_id === realTask.user_id
  )
}
